/**
 * Single source of truth for app routes and navigation.
 * Use ROUTES for links, redirects, and revalidatePath.
 * Use SIDEBAR_NAV and BOTTOM_TABS_NAV for sidebar and mobile bottom tabs.
 */

import type { LucideIcon } from "lucide-react";
import { MARKETING_URL } from "@config/constants";
import { CarFront, CreditCard, User, Star } from "lucide-react";

// ─── Route paths (use these everywhere: Link, router.push, revalidatePath) ───
export const ROUTES = {
  /** App home (authenticated). Use for nav and post-sign-in redirect. */
  HOME: "/home",
  /** App root entry. */
  LANDING: "/",
  ACCOUNT: "/account",
  PROFILE: "/account/profile",
  FEEDBACK: "/account/feedback",
  COMMUTES: "/commutes",
  COMMUTE_DETAIL: (id: string) => `/commutes/${id}`,
  CARS: "/account/car",
  REQUESTS: "/requests",
  REQUEST_DETAIL: (id: string) => `/requests/${id}`,
  BILLING: "/account/billing",
  NOTIFICATIONS: "/notifications",
  NOTIFICATION_SETTINGS: "/account/notifications",
  TERMS: `${MARKETING_URL}/terms`,
  PRIVACY: `${MARKETING_URL}/privacy`,
  AUTH: {
    SIGNIN: "/auth/signin",
    SIGNUP: "/auth/signup",
    FORGET_PASSWORD: "/auth/forget-password",
    RESET_PASSWORD: "/auth/reset-password",
    VERIFY: "/auth/verify",
    CALLBACK: "/auth/callback",
  },
  SEARCH: "/search",
} as const;

export type RouteId = keyof Omit<typeof ROUTES, "AUTH" | "REQUEST_DETAIL">;

// ─── Header nav (desktop persistent: Home, Routes, Requests) ───
export type HeaderNavItem = {
  id: string;
  label: string;
  iconPath: string;
  href: string;
  driverOnly?: boolean;
};

export function getHeaderNavItems(): HeaderNavItem[] {
  return [
    {
      id: "home",
      label: "Explore",
      iconPath: "/icons/svg/map.svg",
      href: ROUTES.HOME,
    },
    {
      id: "commutes",
      label: "Routes",
      iconPath: "/icons/svg/route.svg",
      href: ROUTES.COMMUTES,
    },
    {
      id: "requests",
      label: "Requests",
      iconPath: "/icons/svg/inbox.svg",
      href: ROUTES.REQUESTS,
    },
    // { id: "account", label: "Account", iconPath: "/icons/svg/user-circle.svg", href: ROUTES.ACCOUNT },
  ];
}

// ─── Sidebar nav (avatar-triggered drawer: account only) ───
export type SidebarNavItem = {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  href: string;
  driverOnly?: boolean;
};

export function getSidebarNavItems(isDriver: boolean): SidebarNavItem[] {
  return [
    {
      id: "profile",
      label: "Profile",
      description: "Personal details and preferences",
      icon: User,
      href: ROUTES.PROFILE,
    },
    {
      id: "feedback",
      label: "Feedback",
      description: "Ratings you received",
      icon: Star,
      href: ROUTES.FEEDBACK,
    },
    ...(isDriver
      ? [
          {
            id: "cars",
            label: "Car",
            description: "Manage your car details",
            icon: CarFront,
            href: ROUTES.CARS,
            driverOnly: true,
          },
        ]
      : []),
    ...(isDriver
      ? [
          {
            id: "billing",
            label: "Billing",
            description: "Credits & payments",
            icon: CreditCard,
            href: ROUTES.BILLING,
            driverOnly: true,
          },
        ]
      : []),
  ];
}

// ─── Mobile bottom tabs ───
export type BottomTabItem = {
  id: string;
  label: string;
  iconPath: string;
  href: string;
};

export const BOTTOM_TABS_NAV: BottomTabItem[] = [
  {
    id: "home",
    label: "Explore",
    iconPath: "/icons/svg/map.svg",
    href: ROUTES.HOME,
  },
  {
    id: "commutes",
    label: "Routes",
    iconPath: "/icons/svg/route.svg",
    href: ROUTES.COMMUTES,
  },
  {
    id: "requests",
    label: "Requests",
    iconPath: "/icons/svg/inbox.svg",
    href: ROUTES.REQUESTS,
  },
  {
    id: "account",
    label: "Account",
    iconPath: "/icons/svg/user-circle.svg",
    href: ROUTES.ACCOUNT,
  },
];

export const PROTECTED_PATH_PREFIXES = [
  ROUTES.HOME,
  ROUTES.ACCOUNT,
  ROUTES.COMMUTES,
  ROUTES.REQUESTS,
  ROUTES.NOTIFICATIONS,
] as const;

export function isProtectedPath(pathname: string): boolean {
  return PROTECTED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function isAuthPath(pathname: string): boolean {
  return pathname.startsWith("/auth");
}
